import { normalizeSettingsApi } from 'one-ts-config';
import { AuthApiConfig, AuthTsConfig } from './types';

const DEFAULT_TOKEN_URL = 'adpac/v1/getauthtoken';
const DEFAULT_TOKEN_TYPE = 'Bearer';
const DEFAULT_REFRESH_SKEW_SECONDS = 60;
const SESSION_STORAGE_KEY = 'ccw-auth-access-token';

/** Static auth_api_key from .env (AUTH_API_KEY). Not sourced from ts-config. */
const AUTH_API_KEY = process.env.AUTH_API_KEY || '';

const resolveAuthApiKey = (): string | undefined => {
    const trimmed = AUTH_API_KEY.trim();
    return trimmed || undefined;
};

interface CachedToken {
    accessToken: string;
    expiresAt: number;
    tokenType?: string;
}

interface ParsedTokenResponse {
    accessToken: string;
    expiresIn?: number;
    validUpto?: number;
    tokenType?: string;
}

let cachedToken: CachedToken | null = null;
let inFlightTokenRequest: Promise<string> | null = null;
let activeAuthConfigKey: string | null = null;

const joinUrl = (baseUrl: string, path: string): string => {
    const normalizedBase = baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`;
    const normalizedPath = path.startsWith('/') ? path.slice(1) : path;
    return `${normalizedBase}${normalizedPath}`;
};

const getValueAtPath = (payload: unknown, path?: string): unknown => {
    if (!path) {
        return undefined;
    }

    return path.split('.').reduce<unknown>((current, segment) => {
        if (!current || typeof current !== 'object') {
            return undefined;
        }

        return (current as Record<string, unknown>)[segment];
    }, payload);
};

const readSessionToken = (): CachedToken | null => {
    try {
        const raw = sessionStorage.getItem(SESSION_STORAGE_KEY);
        if (!raw) {
            return null;
        }

        const parsed = JSON.parse(raw) as CachedToken;
        if (!parsed?.accessToken || !parsed?.expiresAt) {
            return null;
        }

        return parsed;
    } catch {
        return null;
    }
};

const writeSessionToken = (token: CachedToken | null): void => {
    if (!token) {
        sessionStorage.removeItem(SESSION_STORAGE_KEY);
        return;
    }

    sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(token));
};

const resolveAuthApiConfig = (tsConfig: AuthTsConfig): AuthApiConfig | null => {
    const authApi = tsConfig.authApi;
    if (!authApi?.enabled) {
        return null;
    }

    const settingsApi = normalizeSettingsApi(tsConfig.settingsApi);
    const baseUrl = authApi.baseUrl ?? settingsApi.baseUrl;

    if (!baseUrl) {
        throw new Error('authApi is enabled but no baseUrl or settingsApi.baseUrl is configured');
    }

    return {
        ...authApi,
        baseUrl,
        url: authApi.url ?? DEFAULT_TOKEN_URL,
        method: authApi.method ?? 'POST',
        tokenType: authApi.tokenType ?? DEFAULT_TOKEN_TYPE,
        refreshSkewSeconds: authApi.refreshSkewSeconds ?? DEFAULT_REFRESH_SKEW_SECONDS,
    };
};

const getAuthConfigKey = (authApi: AuthApiConfig): string => JSON.stringify({
    baseUrl: authApi.baseUrl,
    url: authApi.url,
    method: authApi.method,
    clientId: authApi.clientId,
    clientSecret: authApi.clientSecret,
    apiKey: resolveAuthApiKey(),
    body: authApi.body,
    tokenPath: authApi.tokenPath,
    expiresInPath: authApi.expiresInPath,
    validUptoPath: authApi.validUptoPath,
    tokenTypePath: authApi.tokenTypePath,
    tokenType: authApi.tokenType,
});

const readFirstMatchingPath = (
    payload: unknown,
    pathCandidates: string[],
): unknown => {
    for (let index = 0; index < pathCandidates.length; index += 1) {
        const candidate = getValueAtPath(payload, pathCandidates[index]);
        if (candidate !== undefined && candidate !== null && candidate !== '') {
            return candidate;
        }
    }

    return undefined;
};

const parseNumericValue = (value: unknown): number | undefined => {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
    }

    if (typeof value === 'string' && value.trim() && !Number.isNaN(Number(value))) {
        return Number(value);
    }

    return undefined;
};

const parseValidUpto = (value: unknown): number | undefined => {
    if (typeof value !== 'string' || !value.trim()) {
        return undefined;
    }

    const parsed = Date.parse(value.trim());
    return Number.isFinite(parsed) ? parsed : undefined;
};

const resolveTokenExpiresAt = (
    expiresIn?: number,
    validUpto?: number,
): number => {
    if (validUpto && Number.isFinite(validUpto)) {
        return validUpto;
    }

    if (expiresIn && expiresIn > 0) {
        return Date.now() + (expiresIn * 1000);
    }

    return Date.now() + (3600 * 1000);
};

const isTokenValid = (token: CachedToken | null, refreshSkewSeconds: number): token is CachedToken => {
    if (!token?.accessToken || !token.expiresAt) {
        return false;
    }

    return token.expiresAt > Date.now() + (refreshSkewSeconds * 1000);
};

const getCachedToken = (refreshSkewSeconds: number): CachedToken | null => {
    if (isTokenValid(cachedToken, refreshSkewSeconds)) {
        return cachedToken;
    }

    const sessionToken = readSessionToken();
    if (isTokenValid(sessionToken, refreshSkewSeconds)) {
        cachedToken = sessionToken;
        return sessionToken;
    }

    return null;
};

const parseTokenResponse = (
    payload: unknown,
    authApi: AuthApiConfig,
): ParsedTokenResponse => {
    const tokenPathCandidates = [
        authApi.tokenPath,
        'access_token',
        'token',
        'output.access_token',
        'output.token',
        'results.0.access_token',
        'results.0.token',
    ].filter(Boolean) as string[];

    const accessTokenValue = readFirstMatchingPath(payload, tokenPathCandidates);
    const accessToken = typeof accessTokenValue === 'string' && accessTokenValue.trim()
        ? accessTokenValue.trim()
        : undefined;

    if (!accessToken) {
        throw new Error('Token API response did not include an access token');
    }

    const expiresIn = parseNumericValue(readFirstMatchingPath(payload, [
        authApi.expiresInPath,
        'expires_in',
        'output.expires_in',
        'results.0.expires_in',
    ].filter(Boolean) as string[]));

    const validUpto = parseValidUpto(readFirstMatchingPath(payload, [
        authApi.validUptoPath,
        'valid_upto',
        'output.valid_upto',
        'results.0.valid_upto',
    ].filter(Boolean) as string[]));

    const tokenTypeValue = readFirstMatchingPath(payload, [
        authApi.tokenTypePath,
        'token_type',
        'output.token_type',
        'results.0.token_type',
    ].filter(Boolean) as string[]);

    const tokenType = typeof tokenTypeValue === 'string' && tokenTypeValue.trim()
        ? tokenTypeValue.trim()
        : undefined;

    return {
        accessToken,
        expiresIn,
        validUpto,
        tokenType,
    };
};

const buildTokenRequestParams = (authApi: AuthApiConfig): Record<string, string> => {
    const params: Record<string, string> = {};

    Object.entries(authApi.body ?? {}).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
            params[key] = String(value);
        }
    });

    if (authApi.clientId) {
        params.client_id = authApi.clientId;
    }
    if (authApi.clientSecret) {
        params.client_secret = authApi.clientSecret;
    }

    const apiKey = resolveAuthApiKey();
    if (apiKey) {
        params.auth_api_key = apiKey;
    }

    return params;
};

const buildTokenRequestUrl = (
    authApi: AuthApiConfig,
    baseUrl: string,
    path: string,
): string => {
    const endpoint = joinUrl(baseUrl, path);
    const params = new URLSearchParams(buildTokenRequestParams(authApi));
    const query = params.toString();

    return query ? `${endpoint}?${query}` : endpoint;
};

const buildTokenRequestBody = (authApi: AuthApiConfig): string | undefined => {
    const payload = buildTokenRequestParams(authApi);

    if (Object.keys(payload).length === 0) {
        return undefined;
    }

    return JSON.stringify(payload);
};

const storeToken = (
    parsedToken: ParsedTokenResponse,
): string => {
    cachedToken = {
        accessToken: parsedToken.accessToken,
        expiresAt: resolveTokenExpiresAt(parsedToken.expiresIn, parsedToken.validUpto),
        tokenType: parsedToken.tokenType,
    };
    writeSessionToken(cachedToken);
    return parsedToken.accessToken;
};

const resolveAuthorizationTokenType = (
    authApi: AuthApiConfig,
    token?: CachedToken | null,
): string => (
    token?.tokenType
    ?? authApi.tokenType
    ?? DEFAULT_TOKEN_TYPE
);

export const isAuthApiEnabled = (tsConfig: AuthTsConfig): boolean => (
    resolveAuthApiConfig(tsConfig) !== null
);

export const clearAccessToken = (): void => {
    cachedToken = null;
    inFlightTokenRequest = null;
    activeAuthConfigKey = null;
    writeSessionToken(null);
};

export const prefetchAccessToken = (
    tsConfig: AuthTsConfig,
    fetchImpl?: typeof fetch,
): Promise<string | undefined> => {
    if (!isAuthApiEnabled(tsConfig)) {
        return Promise.resolve(undefined);
    }

    return getAccessToken(tsConfig, fetchImpl).catch(() => undefined);
};

export const getAccessToken = async (
    tsConfig: AuthTsConfig,
    fetchImpl?: typeof fetch,
): Promise<string | undefined> => {
    const authApi = resolveAuthApiConfig(tsConfig);
    if (!authApi) {
        return undefined;
    }

    const resolvedFetch = fetchImpl ?? fetch;

    const authConfigKey = getAuthConfigKey(authApi);
    if (activeAuthConfigKey && activeAuthConfigKey !== authConfigKey) {
        clearAccessToken();
    }
    activeAuthConfigKey = authConfigKey;

    const refreshSkewSeconds = authApi.refreshSkewSeconds ?? DEFAULT_REFRESH_SKEW_SECONDS;
    const existingToken = getCachedToken(refreshSkewSeconds);
    if (existingToken) {
        return existingToken.accessToken;
    }

    if (inFlightTokenRequest) {
        return inFlightTokenRequest;
    }

    inFlightTokenRequest = (async () => {
        const method = authApi.method ?? 'POST';
        const endpoint = method === 'GET'
            ? buildTokenRequestUrl(authApi, authApi.baseUrl as string, authApi.url as string)
            : joinUrl(authApi.baseUrl as string, authApi.url as string);
        const body = method === 'POST' ? buildTokenRequestBody(authApi) : undefined;

        const response = await resolvedFetch(endpoint, {
            method,
            cache: 'no-cache',
            headers: {
                'Content-Type': 'application/json',
            },
            body,
        });

        const responseBody = await response.text();
        if (!response.ok) {
            throw new Error(`Token API request failed with status ${response.status}: ${responseBody}`);
        }

        const trimmed = responseBody.trim();
        if (trimmed.startsWith('<!DOCTYPE') || trimmed.startsWith('<html')) {
            throw new Error('Token API returned HTML instead of JSON. Verify the token endpoint URL.');
        }

        const payload = trimmed ? JSON.parse(trimmed) : {};
        const parsedToken = parseTokenResponse(payload, authApi);
        return storeToken(parsedToken);
    })();

    try {
        return await inFlightTokenRequest;
    } finally {
        inFlightTokenRequest = null;
    }
};

export const getAuthorizationHeader = async (
    tsConfig: AuthTsConfig,
    fetchImpl?: typeof fetch,
): Promise<string | undefined> => {
    const authApi = resolveAuthApiConfig(tsConfig);
    if (!authApi) {
        return undefined;
    }

    const accessToken = await getAccessToken(tsConfig, fetchImpl);
    if (!accessToken) {
        return undefined;
    }

    const tokenType = resolveAuthorizationTokenType(authApi, cachedToken);
    return `${tokenType} ${accessToken}`;
};
